import { HttpError } from '@bits/fetcher';

import type { paths, components } from './openapi';

export type RPCPath = keyof paths;
export type RPCProcedure<T extends RPCPath> =
  paths[T] extends { post: infer P }
    ? P extends { requestBody?: { content: { 'application/json': infer B } } }
      ? B
      : undefined
    : undefined;

export type RPCResponse<T extends RPCPath> =
  paths[T] extends { post: infer P }
    ? P extends { responses: { 200: { content: { 'application/json': infer R } } } }
      ? R
      : unknown
    : unknown;

export type RPCSchemas = components['schemas'];

type FetchParams = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

export class BitsRpcClient {
  constructor(
    public readonly getAccessToken: () => Promise<string>
  ) {}

  async call<T extends RPCPath>(
    procedure: T,
    body?: RPCProcedure<T>,
    requestInit: FetchParams = {}
  ): Promise<RPCResponse<T>> {
    const accessToken = await this.getAccessToken();
    const response = await fetch('/rpc' + procedure, {
      ...requestInit,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',

        // Only set the content type if there is a body
        ...(Boolean(body) && { 'Content-Type': 'application/json' }),
        
        // Use the provided correlation ID or generate a new one
        'bits-correlation-id':
        requestInit?.headers?.['bits-correlation-id'] || crypto.randomUUID(),

        // Allow overriding all headers
        ...requestInit?.headers,
      },
      method: 'POST',
      ...(Boolean(body) && { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new HttpError(
        response.status,
        response.statusText,
        text,
        response.headers.get('bits-correlation-id') || undefined
      );
    }

    const text = await response.text();

    if (text === '') {
      // @ts-expect-error - This is a valid response
      return null;
    }

    return JSON.parse(text);
  }
}
